import React, { useMemo } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  Box,
  InputGroup,
  InputLeftElement,
  Input,
} from "@chakra-ui/react";
import { ChevronDownIcon, AddIcon, SearchIcon } from "@chakra-ui/icons";
import { ColumnInfo, Header } from "build-ts/metadata";
import { ColumnType } from "build-ts/enums";

interface DataModelMenuComponentProps {
  type: "KG_COLUMN" | "METRIC";
  logicalColumnList: ColumnInfo[];
  kgColumnList?: Header[];
  metricList?: Header[];
  selectedItem: Header | null;
  columnType?: ColumnType;
  columnIndex: number;
  logicalColumn: ColumnInfo;
  onItemSelect: (item: Header | null) => void;
  onOpenCreateKGColumnModal: (columnType: ColumnType, columnIndex: number, defaultName: string) => void;
  // Opens the full metric definition modal for the given row
  onOpenCreateMetricModal: (columnIndex: number, logicalColumn: ColumnInfo) => void;
  dependentKgColumns?: Map<string, Header[]>;
  placeholder?: string;
}

const DataModelMenuComponent: React.FC<DataModelMenuComponentProps> = React.memo(({
  type,
  logicalColumnList,
  kgColumnList,
  metricList,
  selectedItem,
  columnType,
  columnIndex,
  logicalColumn,
  onItemSelect,
  onOpenCreateKGColumnModal,
  onOpenCreateMetricModal,
  dependentKgColumns,
  placeholder = "None",
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  // Memoize already mapped KG columns to avoid recalculation on every render
  const alreadyMappedKgColumns = useMemo(() => {
    if (type !== "KG_COLUMN" || !dependentKgColumns) return [];
    
    const mappedColumns: Header[] = [];
    
    logicalColumnList.forEach((column, index) => {
      // Skip the current column
      if (index === columnIndex) return;
      
      const kgColumnMapping = dependentKgColumns.get(column.header?.id || "");
      if (kgColumnMapping && kgColumnMapping.length > 0) {
        mappedColumns.push(...kgColumnMapping);
      }
    });
    
    return mappedColumns;
  }, [type, dependentKgColumns, logicalColumnList, columnIndex]);

  // Get the appropriate list based on type
  const itemList = type === "KG_COLUMN" ? kgColumnList : metricList;

  // Filter items based on search term and column type (for KG columns)
  const filteredItems = useMemo(() => {
    if (!itemList) return [];
    
    let filtered = itemList.filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // For KG columns, filter by column type if provided
    if (type === "KG_COLUMN" && columnType) {
      filtered = filtered.filter(item => item.column_type === columnType);
    }

    return filtered;
  }, [itemList, searchTerm, type, columnType]);

  return (
    <Menu placement="bottom-start" closeOnSelect={true}>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        size="sm"
        variant="outline"
        textAlign="left"
        width="170px"
        justifyContent="space-between"
        overflow="hidden"
      >
        <Text 
          isTruncated 
          maxWidth="120px"
          title={selectedItem?.name || placeholder}
          m={0}
        >
          {selectedItem?.name || placeholder}
        </Text>
      </MenuButton>
      <MenuList maxHeight="250px" overflowY="auto" zIndex={1000}>
        {/* Search Bar */}
        <Box 
          px={3} 
          pb={2}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder={`Search ${type === "KG_COLUMN" ? "KG columns" : "metrics"}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outline"
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus={false}
            />
          </InputGroup>
        </Box>

        {/* None option */}
        {type === "KG_COLUMN" && (
          <MenuItem onClick={() => onItemSelect(null)}>
            None
          </MenuItem>
        )}

        {/* Create New option — opens modal for both KG Column and Metric */}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            if (type === "KG_COLUMN") {
              onOpenCreateKGColumnModal(columnType!, columnIndex, logicalColumn.header?.name || "");
            } else {
              onOpenCreateMetricModal(columnIndex, logicalColumn);
            }
          }}
          closeOnSelect={true}
          icon={<AddIcon />}
          color="purple.600"
          fontWeight="bold"
        >
          Create New {type === "KG_COLUMN" ? "KG Column" : "Metric"}
        </MenuItem>

         {/* Filtered Items */}
         {filteredItems?.length && filteredItems?.length > 0 ? (
           filteredItems?.map((item) => {
             // Check if this KG column is already mapped to another logical column (not the current one)
             const isAlreadyMapped = type === "KG_COLUMN" && 
               alreadyMappedKgColumns.some(mappedColumn => mappedColumn.id === item.id) &&
               selectedItem?.id !== item.id; // Allow current selection to remain enabled
             
             return (
               <MenuItem 
                 key={item.id} 
                 onClick={() => {
                   if (!isAlreadyMapped) {
                     onItemSelect(item);
                   }
                 }}
                 isDisabled={isAlreadyMapped}
                 opacity={isAlreadyMapped ? 0.5 : 1}
                 cursor={isAlreadyMapped ? "not-allowed" : "pointer"}
                 bg={selectedItem?.id === item.id ? "purple.100" : "surface.default"}
               >
                 {item.name}
               </MenuItem>
             );
           })
        ) : (
          searchTerm && (
            <MenuItem isDisabled>
              No {type === "KG_COLUMN" ? "KG columns" : "metrics"} found
            </MenuItem>
          )
        )}
      </MenuList>
    </Menu>
  );
});

export default DataModelMenuComponent;